/**
 * @file StandingOrdersWidget.js
 * @version 2.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-02-02
 * @modified 2026-10-15
 *
 * 2.3.0 (2026-10-15, Cam): a standing order whose main product is archived
 * carries a "Product archived" badge — no order can be made from it until its
 * product is changed (utils/standingOrderProducts).
 *
 * The week's standing orders, a day at a time.
 *
 * Restored to the shape it had before — day tabs with counts, a real table, a
 * contact popover on the customer name, zoom on the header — with two changes:
 *
 *   1. It reads the local database through /api/standing-orders. The old
 *      version fetched a Google Sheet through a URL baked into the bundle; the
 *      sheet is no longer maintained, and the system is now the record.
 *
 *   2. It carries "last had": when that customer actually last received that
 *      product. A standing order says what someone is MEANT to get, and on its
 *      own cannot say whether they are getting it — which is the question worth
 *      putting on a dashboard. Anything a fortnight past its schedule is marked.
 *
 * Tomorrow is the opening tab rather than today, because the reason to look at
 * this board is to see what has to be made.
 */

import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { API_URL } from '../config';
import LoadingSpinner from '../components/LoadingSpinner';
import ContactPopover from '../components/ContactPopover';
import { formatQuantity } from '../utils/numberFormatters';
import { withAdditives } from '../utils/additiveLabel';
import { mainProductArchived, mainProductOf, productLabel } from '../utils/standingOrderProducts';
import './StandingOrdersWidget.css';

const DAY_NAMES = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Interval'];
const FULL_DAY_NAMES = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Interval'];

/** Tomorrow, clamped into the working week — nothing is made at the weekend. */
function tomorrowWorkingDay() {
  const day = (new Date().getDay() + 1) % 7;
  return (day === 0 || day === 6) ? 1 : day;
}

function daysSince(value) {
  if (!value) return null;
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return null;
  return Math.floor((Date.now() - then.getTime()) / 86400000);
}

/**
 * How long a schedule may go before it has plainly stopped: twice the interval
 * plus a few days' slack, so a weekly order three days late is not shouted
 * about but one that has missed two cycles is.
 */
function overdueAfter(order) {
  if (order.schedule_type === 'weekly') return 7 * 2 + 3;
  const n = Number(order.interval_value) || 1;
  const unit = String(order.interval_type || 'days').toLowerCase();
  const days = unit.startsWith('week') ? n * 7 : unit.startsWith('month') ? n * 30 : n;
  return days * 2 + 3;
}

function ago(days) {
  if (days === null) return 'never';
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return weeks < 9 ? `${weeks}w ago` : `${Math.floor(days / 30)}m ago`;
}

function StandingOrdersWidget({ settings = {}, onSettingsChange, heightStyle }) {
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState(settings.zoom || 12);
  const [selectedDay, setSelectedDay] = useState(settings.selectedDay || tomorrowWorkingDay());
  const [popover, setPopover] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/standing-orders/search?isActive=true&limit=500`, {
          credentials: 'include'
        });
        const data = await res.json();
        if (!cancelled) setOrders(data.standingOrders || []);
      } catch (_) {
        if (!cancelled) setError('Could not load standing orders');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (settings.zoom !== undefined && settings.zoom !== zoom) setZoom(settings.zoom);
    if (settings.selectedDay !== undefined && settings.selectedDay !== selectedDay) {
      setSelectedDay(settings.selectedDay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.zoom, settings.selectedDay]);

  const handleZoomChange = (next) => {
    setZoom(next);
    if (onSettingsChange) onSettingsChange({ ...settings, zoom: next });
  };

  const handleDayChange = (day) => {
    setSelectedDay(day);
    if (onSettingsChange) onSettingsChange({ ...settings, selectedDay: day });
  };

  const handleNameClick = (e, name) => {
    if (!name) return;
    e.stopPropagation();
    setPopover({ name, type: 'customer', anchorEl: e.currentTarget });
  };

  const formatQty = (qty) => {
    const shown = formatQuantity(qty);
    // Capital T, per the project rule.
    return shown ? `${shown} T` : '-';
  };

  /* Grouped by the tab each order belongs to: 1-5 by weekday, 6 for intervals. */
  const byDay = useMemo(() => {
    const groups = {};
    for (const o of orders || []) {
      const key = o.schedule_type === 'interval' ? 6 : Number(o.day_of_week);
      if (!key) continue;
      (groups[key] = groups[key] || []).push(o);
    }
    for (const list of Object.values(groups)) {
      list.sort((a, b) => String(a.customer_name || '').localeCompare(String(b.customer_name || '')));
    }
    return groups;
  }, [orders]);

  const dayOrders = byDay[selectedDay] || [];
  const isInterval = selectedDay === 6;

  return (
    <div className="dashboard-card standing-orders-card" style={heightStyle}>
      <div className="card-header">
        <h3>
          <span className="material-symbols-rounded">event_repeat</span>
          Standing Orders
        </h3>
        <div className="header-controls">
          <div className="zoom-controls">
            <button className="zoom-btn" onClick={() => handleZoomChange(Math.max(9, zoom - 1))}>−</button>
            <button className="zoom-btn" onClick={() => handleZoomChange(Math.min(18, zoom + 1))}>+</button>
          </div>
        </div>
      </div>

      <div className="day-tabs">
        {[1, 2, 3, 4, 5, 6].map((day) => (
          <button
            key={day}
            className={`day-tab ${day === selectedDay ? 'active' : ''}`}
            onClick={() => handleDayChange(day)}
          >
            {DAY_NAMES[day]}
            <span className="tab-count">{(byDay[day] || []).length}</span>
          </button>
        ))}
      </div>

      <div className="card-body scrollable-body">
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="empty-state">{error}</div>
        ) : dayOrders.length === 0 ? (
          <div className="empty-state">No orders for {FULL_DAY_NAMES[selectedDay]}</div>
        ) : (
          <div className="standing-orders-table" style={{ fontSize: zoom }}>
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Product</th>
                  <th className="col-qty">Qty</th>
                  <th className="col-lasthad">Last / next</th>
                  {isInterval && <th className="col-interval">Frequency</th>}
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {dayOrders.map((order) => {
                  const days = daysSince(order.last_had_at);
                  const booked = !!order.next_due_at;
                  // Nothing is late if it is already on order.
                  const late = !booked && (days === null || days > overdueAfter(order));
                  return (
                    <tr key={order.id}>
                      <td>
                        <span
                          className="clickable-name"
                          onClick={(e) => handleNameClick(e, order.customer_name)}
                        >{order.customer_name || order.customer_code}</span>
                      </td>
                      {/* Every accepted product, main first — alternatives read
                          "A / B" — each with its own additives: "Product Name +
                          Additive (Code)", the code left off when it is the name. */}
                      <td>{(order.products || []).length
                        ? order.products.map(p => withAdditives(p.item_name, p.additives)).join(' / ')
                        : (order.product_name || order.product_code)}
                        {mainProductArchived(order) && (
                          <span
                            className="so-widget-archived"
                            title={`${productLabel(mainProductOf(order))} is archived — no order can be made from this standing order until its product is changed`}
                          >Product archived</span>
                        )}</td>
                      <td className="col-qty">{formatQty(order.quantity)}</td>
                      {/* The point of the column is the ones that have stopped,
                          so lateness is what carries the colour. */}
                      {/* Already ordered wins: the column is here so nobody
                          raises a second order for a delivery already coming. */}
                      <td className={`col-lasthad${booked ? ' is-booked' : late ? ' is-late' : ''}`}
                          title={booked
                            ? `Already ordered${order.next_due_reference ? ` — ${order.next_due_reference}` : ''}`
                            : order.last_had_reference
                              ? `${order.last_had_source || ''} ${order.last_had_reference}`.trim()
                              : undefined}>
                        {booked
                          ? new Date(order.next_due_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                          : ago(days)}
                      </td>
                      {isInterval && (
                        <td className="col-interval">
                          {order.interval_value
                            ? `${order.interval_value} ${order.interval_type || ''}`.trim()
                            : '-'}
                        </td>
                      )}
                      <td>{order.notes && <span className="notes-value">{order.notes}</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {popover && ReactDOM.createPortal(
        <ContactPopover
          name={popover.name}
          type={popover.type}
          anchorEl={popover.anchorEl}
          onClose={() => setPopover(null)}
        />,
        document.body
      )}
    </div>
  );
}

export default StandingOrdersWidget;
